import { useEffect, useState, useRef } from "react";
import api, { errorMessage } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { RefreshCw, Timer, CircleDot, Save } from "lucide-react";
import { toast } from "sonner";

const STAGE_STYLES = {
  green: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  yellow: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  red: "bg-red-500/20 text-red-300 border-red-500/40 animate-pulse",
};

function fmtElapsed(sec) {
  if (sec < 60) return `${Math.floor(sec)}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${Math.floor(sec % 60)}s`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h ${m}m`;
}

function StageBadge({ stage }) {
  return (
    <Badge className={`${STAGE_STYLES[stage]} font-bold uppercase tracking-wider text-[10px]`} data-testid={`stage-${stage}`}>
      <CircleDot className="w-3 h-3 mr-1" /> {stage}
    </Badge>
  );
}

function SLAConfigCard({ onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/settings/sla").then(r => setCfg(r.data)).catch(e => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        telecaller_green_min: Number(cfg.telecaller_green_min),
        telecaller_yellow_min: Number(cfg.telecaller_yellow_min),
        field_green_hr: Number(cfg.field_green_hr),
        field_yellow_hr: Number(cfg.field_yellow_hr),
        bank_staff_green_hr: Number(cfg.bank_staff_green_hr),
        bank_staff_yellow_hr: Number(cfg.bank_staff_yellow_hr),
      };
      await api.post("/admin/settings/sla", body);
      toast.success("SLA thresholds updated");
      load();
      onSaved && onSaved();
    } catch (e) { toast.error(errorMessage(e)); }
    setSaving(false);
  };
  if (!cfg) return null;
  const set = (k, v) => setCfg({ ...cfg, [k]: v.replace(/\D/g, "") });
  const groups = [
    { key: "telecaller", title: "Telecaller SLA", unit: "minutes", green: "telecaller_green_min", yellow: "telecaller_yellow_min", note: "How long a lead can sit with a Telecaller before turning yellow / red." },
    { key: "field", title: "Field Staff SLA", unit: "hours", green: "field_green_hr", yellow: "field_yellow_hr", note: "How long a lead can sit with Field Staff before turning yellow / red." },
    { key: "bank_staff", title: "Bank Staff SLA", unit: "hours", green: "bank_staff_green_hr", yellow: "bank_staff_yellow_hr", note: "Applies to cases in bank pool, taken, and awaiting payment." },
  ];
  return (
    <div className="card-surface p-6" data-testid="sla-config-card">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="font-heading text-xl text-white flex items-center gap-2"><Timer className="w-5 h-5 text-amber-500"/> SLA Thresholds</div>
          <div className="text-sm text-slate-500">Set Green → Yellow → Red timers per role. Applied live in the monitor below.</div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="sla-save-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
          <Save className="w-4 h-4 mr-2"/>{saving ? "Saving..." : "Save Thresholds"}
        </Button>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {groups.map(g => (
          <div key={g.key} className="bg-slate-950/60 border border-slate-800 rounded-xl p-4" data-testid={`sla-group-${g.key}`}>
            <div className="font-heading text-white mb-2">{g.title}</div>
            <div className="text-xs text-slate-500 mb-3">{g.note}</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-emerald-400 text-[10px] uppercase tracking-widest">Green ≤ ({g.unit})</Label>
                <Input data-testid={`sla-${g.key}-green`} value={cfg[g.green]} onChange={e => set(g.green, e.target.value)} className="bg-slate-900 border-slate-700 mt-2"/>
              </div>
              <div>
                <Label className="text-amber-400 text-[10px] uppercase tracking-widest">Yellow ≤ ({g.unit})</Label>
                <Input data-testid={`sla-${g.key}-yellow`} value={cfg[g.yellow]} onChange={e => set(g.yellow, e.target.value)} className="bg-slate-900 border-slate-700 mt-2"/>
              </div>
            </div>
            <div className="text-[10px] text-red-400 mt-3 uppercase tracking-widest">Above Yellow = Red 🔴</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SLAMonitorPage() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/sla-monitor");
      setData(data);
    } catch (e) { toast.error(errorMessage(e)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoRefresh) {
      timerRef.current = setInterval(load, 60_000);
    }
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [autoRefresh]);

  const rows = (data?.rows || []).filter(r => filter === "all" || r.stage === filter);
  const counts = data?.counts || { green: 0, yellow: 0, red: 0 };

  return (
    <div className="space-y-6" data-testid="sla-monitor-page">
      <SLAConfigCard onSaved={load} />

      <div className="card-surface p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="font-heading text-xl text-white flex items-center gap-2"><Timer className="w-5 h-5 text-amber-500"/> Live SLA Monitor</div>
            <div className="text-sm text-slate-500">
              {data?.total || 0} active leads · Auto refresh every 60s ·
              {data?.server_now && <span className="ml-1 font-mono-alt text-slate-400">last sync {new Date(data.server_now).toLocaleTimeString()}</span>}
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <label className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-2">
              <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} data-testid="sla-auto-refresh"/> Auto refresh
            </label>
            <Button onClick={load} data-testid="sla-refresh" variant="secondary" className="bg-slate-800 hover:bg-slate-700 text-white rounded-full">
              <RefreshCw className="w-4 h-4 mr-2"/>Refresh
            </Button>
          </div>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { key: "green", label: "Green (On Time)", color: "text-emerald-400", bg: "border-emerald-500/30" },
            { key: "yellow", label: "Yellow (Warning)", color: "text-amber-400", bg: "border-amber-500/30" },
            { key: "red", label: "Red (Breached)", color: "text-red-400", bg: "border-red-500/30" },
          ].map(c => (
            <button
              key={c.key}
              onClick={() => setFilter(filter === c.key ? "all" : c.key)}
              data-testid={`sla-count-${c.key}`}
              className={`bg-slate-950/60 border ${c.bg} rounded-xl p-4 text-left transition-transform ${filter === c.key ? "scale-[1.02]" : "hover:scale-[1.01]"}`}
            >
              <div className={`font-display text-4xl ${c.color}`}>{counts[c.key] || 0}</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{c.label}</div>
            </button>
          ))}
        </div>

        {filter !== "all" && (
          <div className="mb-3 text-xs text-slate-500">
            Filtering by <StageBadge stage={filter} />
            <button onClick={() => setFilter("all")} className="ml-2 underline">clear</button>
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow className="border-white/5">
              <TableHead>Stage</TableHead>
              <TableHead>Lead</TableHead>
              <TableHead>Phase</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Loan</TableHead>
              <TableHead>Elapsed</TableHead>
              <TableHead>Limits</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.lead_id} className="border-white/5" data-testid={`sla-row-${r.lead_id}`}>
                <TableCell><StageBadge stage={r.stage} /></TableCell>
                <TableCell>
                  <div className="text-white">{r.name}</div>
                  <div className="text-xs text-slate-500 font-mono-alt">{r.lead_id} · {r.phone}</div>
                </TableCell>
                <TableCell className="text-slate-300 text-sm">{r.phase_label}</TableCell>
                <TableCell className="text-slate-300 text-sm">
                  {r.owner_name || <span className="text-slate-600">Unassigned</span>}
                  {r.owner_sub_role && <div className="text-[10px] text-slate-500 uppercase tracking-widest">{r.owner_sub_role.replace("_"," ")}</div>}
                </TableCell>
                <TableCell className="text-slate-300 text-sm">
                  <div>{r.loan_type}</div>
                  <div className="text-xs text-slate-500">₹{Number(r.amount || 0).toLocaleString("en-IN")}</div>
                </TableCell>
                <TableCell className={`font-mono-alt text-sm ${r.stage === "red" ? "text-red-400" : r.stage === "yellow" ? "text-amber-400" : "text-emerald-400"}`}>
                  {fmtElapsed(r.elapsed_seconds)}
                </TableCell>
                <TableCell className="text-[10px] text-slate-500 font-mono-alt">
                  G ≤ {fmtElapsed(r.green_limit_s)}<br/>
                  Y ≤ {fmtElapsed(r.yellow_limit_s)}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">No active leads in this stage</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
